<template>
    <div class="flex flex-col md:flex-row">
        <aside class="w-full md:w-220px">
            <div v-if="lesson" class="mb-5">
                <h5 class="mt-0">{{ course.locale_content.title }}</h5>
                <div class="space-y-4">
                    <div v-for="(lessonGroup, index) in lessonGroups" class="border p-2 rounded-4px">
                        <div class="flex font-medium items-center justify-between pb-4 text-gray-700">
                            <h5 class="my-0">{{ index }}</h5>
                            <div>00:00</div>
                        </div>
                        <ul class="space-y-3">
                            <li class="flex items-center" v-for="lesson in lessonGroup">
                                <div class="mr-1">
                                    <template v-if="lesson.is_accessible">
                                        <div v-if="lesson.lesson === lessonNumber">
                                            <svg class="h-6 text-primary-500 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        <div v-else-if="lesson.watched_at">
                                            <svg class="h-6 text-gray-500 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        <div v-else>
                                            <svg class="cursor-pointer h-6 text-primary-500 w-6" @click="setSelectedLesson(lesson.lesson)" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                    </template>
                                    <template v-else>
                                        <svg class="cursor-not-allowed h-5 text-warning-500 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                        </svg>
                                    </template>
                                </div>
                                <div class="font-medium flex-1 leading-4 px-1" style="overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;">
                                    <template v-if="lesson.lesson === lessonNumber">
                                        <div class="text-primary-500">
                                            {{ lesson.locale_content.title }}
                                        </div>
                                    </template>
                                    <template v-else>
                                        <template v-if="lesson.is_accessible">
                                            <a class="text-gray-500" @click.stop.prevent="setSelectedLesson(lesson.lesson)" href="javascript:;">
                                                {{ lesson.locale_content.title }}
                                            </a>
                                        </template>
                                        <template v-else>
                                            <a class="cursor-not-allowed focus:no-underline hover:no-underline text-gray-500" href="javascript:;">
                                                {{ lesson.locale_content.title }}
                                            </a>
                                        </template>
                                    </template>
                                </div>
                                <div class="ml-1 font-medium" :class="[ lesson.lesson === lessonNumber ? 'text-primary-500' : 'text-gray-500' ]">
                                    00:00
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            <button @click="goToAllCourses" class="btn btn-default btn-block">
                <span class="icon icon-mail-reply icon-lg icon-fw"></span>
                {{ $t('courses.all_courses') }}
            </button>
        </aside>
        <div class="flex-1 md:pl-15px">
            <div class="card">
                <div class="card-body">
                    <div class="row gutter-xs">
                        <div class="bg-black rounded">
                            <template v-if="lesson">
                                <div class="video-container" v-html="lesson.locale_content.source_html"></div>
                            </template>
                            <template v-else>
                                <div class="video-container" v-html="course.locale_content.source_html"></div>
                            </template>
                        </div>
                        <div class="my-4">
                            <div class="bg-center bg-cover flex flex-col items-center md:flex-row px-5 py-5 rounded" style="background-image: url(/images/background-pattern.png);" :class="cardAsideDynamicClasses">
                                <div class="mr-4">
                                    <template v-if="course.topic.slug === 'reefkig-budgets'">
                                        <div class="bg-black-transparent-15 flex h-16 items-center justify-center p-2 rounded-full w-16">
                                            <svg class="block fill-current h-10 text-white w-10" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                                                <path d="M5 19h-4v-4h4v4zm6 0h-4v-8h4v8zm6 0h-4v-13h4v13zm6 0h-4v-19h4v19zm1 2h-24v2h24v-2z"></path>
                                            </svg>
                                        </div>
                                    </template>
                                    <template v-else>
                                        <svg class="h-16 w-16" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                                            <circle style="fill: rgba(0, 0, 0, 0.15);" cx="256" cy="256" r="256"></circle>
                                            <path style="fill: rgba(0, 0, 0, 0.14);" d="M511.822 265.084L314.851 68.113l.739 25.654-26.879-26.879-60.497 106.817-78.762 53.541 33.234 33.234-20.357 28.699-52.697-52.697 20.744 97.745-26.661 37.585 139.887 139.887c4.108.193 8.24.301 12.398.301 138.343 0 251.036-109.741 255.822-246.916z"></path>
                                            <path fill="#f7fafc" d="M431.726 301.68l-37.304-33.293-14.914 16.711-18.698-35.489-44.236 23.306 27.397 52.001-10.625 11.905-44.635 12.201v-67.377l76.658-50.621 41.038 7.273 8.726-49.233-28.634-5.074 8.694-26.623-47.529-15.522-17.051 52.213-41.902 27.669v-70.849l56.438-42.991-30.298-39.774-26.14 19.911V66.887h-65v92.646l-52.083-51.93-35.303 35.408 22.545 22.478-33.016 17.675 23.599 44.081 46.427-24.855 27.831 27.749v91.568l-53.416-13.142 29.447-48.238-42.677-26.052-20.866 34.182-26.567-31.977-38.459 31.952 40.633 48.908-22.145 6.485 14.054 47.985 52.206-15.29 67.79 16.677v57.096h65v-29.437l59.805-16.347 72.249 11.209 7.665-49.409-32.091-4.979z"></path>
                                            <path fill="#e2e8f0" d="M431.726 301.68l-37.304-33.293-14.914 16.711-18.698-35.489-44.236 23.306 27.397 52.001-10.625 11.905-44.635 12.201v-67.377l76.658-50.621 41.038 7.273 8.726-49.233-28.634-5.074 8.694-26.623-47.529-15.522-17.051 52.213-41.902 27.669v-70.849l56.438-42.991-30.298-39.774-26.14 19.911V66.887h-33.116v363.406h33.116v-29.437l59.805-16.347 72.249 11.209 7.665-49.409-32.091-4.979z" class="active-path"></path>
                                        </svg>
                                    </template>
                                </div>
                                <div class="flex-1 md:mx-4 md:my-0 my-3 text-white">
                                    <h4 class="font-bold md:text-left mt-0 text-center text-xl tracking-wide">
                                        <template v-if="lesson">
                                            {{ lesson.locale_content.title }}
                                        </template>
                                        <template v-else>
                                            {{ course.locale_content.title }}
                                        </template>
                                    </h4>
                                    <div class="md:text-left text-center">
                                        <span class="bg-black-transparent-15 font-light px-3 py-1 rounded-full text-2xs text-center uppercase">
                                            {{ course.topic.locale_content.name }}
                                        </span>
                                    </div>
                                </div>
                                <div class="flex flex-col">
                                    <template v-if="nextLesson && nextLesson.is_accessible">
                                        <a @click.stop.prevent="setSelectedLesson(nextLesson.lesson)" class="cursor-pointer focus:no-underline font-medium group hover:no-underline hover:shadow-md inline-flex items-center px-6 py-2 rounded" :class="nextLessonButtonDynamicClasses" href="javascript:;">
                                            <span class="mr-1">{{ $t('courses.next_lesson') }}</span>
                                            <svg class="duration-500 ease-in-out group-hover:translate-x-2 h-5 transform w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                            </svg>
                                        </a>
                                    </template>
                                </div>
                            </div>
                        </div>
                        <div class="card">
                            <div class="card-body">
                                <h4 class="mt-0">{{ $t('courses.transcript') }}</h4>
                                <template v-if="lesson">
                                    <div v-html="lesson.locale_content.transcript"></div>
                                </template>
                                <template v-else>
                                    <div v-html="course.locale_content.transcript"></div>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Player from '@vimeo/player';

export default {
    props: {
        courses: {
            type: Array,
            required: true,
        },
        courseSlug: {
            type: String,
        },
        lessonNumber: {
            type: Number,
        },
    },

    mounted() {
        var iframe = document.querySelector('iframe');
        var player = new Player(iframe);
        player.on('timeupdate', data => this.updateVideoProgress(data));
        player.on('pause', data => this.updateWatchedAtFlag());
        setInterval(() => this.updateWatchedAtFlag(), 7 * 1000);
    },

    data() {
        return {
            selectedLesson: this.lessonNumber,
            videoTracker: {
                progress: [],
                updatedAt: null,
                sentAt: null,
                wasRecentlyMarkedAsWatched: false,
            },
            form: new Form({
                course_id: '',
            }),
        };
    },

    computed: {
        cardAsideDynamicClasses() {
            let backgroundColor = 'bg-primary-500';
            switch (true) {
                case (this.course.topic.slug === 'reefkig-budgets'):
                    backgroundColor = 'bg-blue-500';
                    break;
            }

            return backgroundColor;
        },

        nextLessonButtonDynamicClasses() {
            let classes = 'bg-primary-100 text-primary-500';
            switch (true) {
                case (this.course.topic.slug === 'reefkig-budgets'):
                    classes = 'bg-blue-100 text-blue-500';
                    break;
            }

            return classes;
        },

        course() {
            return this.courses.find(course => course.slug === this.courseSlug);
        },

        lesson() {
            const accessibleLessons = this.course.lessons.filter(lesson => lesson.is_accessible);
            if (accessibleLessons.length === 0) {
                return null;
            }

            return accessibleLessons.find(lesson => lesson.lesson === this.lessonNumber);
        },

        nextLesson() {
            if (this.course.lessons.length === 0) {
                return null;
            }
            const nextLessonNumber = this.lessonNumber + 1;

            return this.course.lessons.find(lesson => lesson.lesson === nextLessonNumber);
        },

        lessonGroups() {
            const self = this;

            const lessonGroups = _.groupBy(this.course.lessons, function (lesson) {
                if (lesson.is_free === true) {
                    return self.$t('courses.preview');
                }
                else if (lesson.is_free === false) {
                    if (self.courseSlug === 'secrets-of-budget-management') {
                        return lesson.lesson < 14
                            ? self.$t('courses.chapter', { chapter: 1 })
                            : self.$t('courses.chapter', { chapter: 2 });
                    }
                    return self.$t('courses.content');
                }
            });

            return lessonGroups;
        },
    },

    watch: {
        selectedLesson: function(value) {
            if (value === this.lessonNumber) {
                return;
            }
            Bus.$emit('openCourse', { course: this.course, lessonNumber: value });
        },
    },

    methods: {
        goToAllCourses() {
            Bus.$emit('openAllCourses');
        },

        setSelectedLesson(lesson) {
            this.selectedLesson = lesson;
        },

        updateVideoProgress(data) {
            const currentPercent = Math.ceil(data.percent * 100);
            if (this.videoTracker.progress.indexOf(currentPercent) != -1) {
                return;
            }

            let timestamp = (new Date()).getTime();
            timestamp = Math.floor(timestamp / 1000);

            this.videoTracker.progress.push(currentPercent);
            this.videoTracker.updatedAt = timestamp;
        },

        updateWatchedAtFlag() {
            if (this.videoTracker.sentAt === this.videoTracker.updatedAt) {
                return;
            }
            this.videoTracker.sentAt = this.videoTracker.updatedAt;

            const isWatched = this.videoTracker.progress.some(percentage => percentage > 80);
            if (isWatched && ! this.videoTracker.wasRecentlyMarkedAsWatched) {
                this.form.course_id = this.lesson ? this.lesson.id : this.course.id;
                Globals.post('/api/v1/mark-course-as-watched', this.form)
                    .then(() => {
                        this.resetForm();
                        this.videoTracker.wasRecentlyMarkedAsWatched = true;
                    })
                    .catch(() => this.resetForm());
            }
        },

        resetForm() {
            this.form = new Form({
                course_id: '',
            });
        },
    },
};
</script>
